import React from "react";
import { useBooking } from "@/contexts/BookingContext";
import { trackEvent } from "@/lib/analytics";

// Public article body renderer. Shared by the public BlogPost page and the
// admin Content Manager preview so both render identically.
export function PostBody({ body, slug }) {
  const { open } = useBooking();
  const rawBlocks = (body || "").split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  const blocks = [];
  for (const block of rawBlocks) {
    const lines = block.split("\n");
    if (lines.length > 1 && /^#{1,3}\s/.test(lines[0])) {
      blocks.push(lines[0]);
      const rest = lines.slice(1).join("\n").trim();
      if (rest) blocks.push(rest);
    } else {
      blocks.push(block);
    }
  }

  const renderInline = (text, prefix) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={j} className="font-medium text-[#451C25]">
            {part.slice(2, -2)}
          </strong>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        if (linkMatch[2] === "/#booking") {
          return <button key={j} type="button" data-testid={`post-booking-${prefix}-${j}`} className="inline text-left underline underline-offset-4 text-[#451C25] hover:text-[#2B1017] transition-colors" onClick={() => {
            const placement = `article_${slug}_contextual`;
            trackEvent("schedule_call_click", { placement, mode: "embedded", country: "spain", page_path: window.location.pathname });
            open(placement);
          }}>{linkMatch[1]}</button>;
        }
        return (
          <a
            key={j}
            data-testid={`post-link-${prefix}-${j}`}
            href={linkMatch[2]}
            target={linkMatch[2].startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="text-[#451C25] underline underline-offset-4 hover:text-[#2B1017]"
          >
            {linkMatch[1]}
          </a>
        );
      }
      return <span key={j}>{part}</span>;
    });
  };

  return (
    <div
      data-testid="blog-post-body"
      className="prose-mte space-y-6 text-[#451C25] text-[17px] leading-[1.8] font-light"
    >
      {blocks.map((b, i) => {
        if (b.startsWith("### ")) {
          return (
            <h3 key={i} className="font-serif text-xl md:text-2xl tracking-tight mt-8 mb-1 text-[#451C25]">
              {renderInline(b.replace(/^###\s+/, ""), i)}
            </h3>
          );
        }
        if (b.startsWith("## ")) {
          return (
            <h2 key={i} className="font-serif text-2xl md:text-3xl tracking-tight mt-10 mb-2 text-[#451C25]">
              {renderInline(b.replace(/^##\s+/, ""), i)}
            </h2>
          );
        }
        if (b.startsWith("> ")) {
          return (
            <blockquote key={i} className="border-l-4 border-[#451C25] pl-5 italic text-[#6B4954]">
              {renderInline(b.replace(/^>\s+/gm, ""), i)}
            </blockquote>
          );
        }
        const isList = b.split("\n").every((line) => /^(\s*[-*•]\s+|\s*\d+\.\s+)/.test(line));
        if (isList) {
          const isOrdered = /^\s*\d+\.\s/.test(b.split("\n")[0]);
          const ListTag = isOrdered ? "ol" : "ul";
          const items = b.split("\n");
          return (
            <ListTag key={i} className={`${isOrdered ? "list-decimal" : "list-disc"} pl-6 space-y-2 marker:text-[#451C25]`}>
              {items.map((it, j) => (
                <li key={j}>
                  {renderInline(it.replace(/^\s*[-*•]\s+/, "").replace(/^\s*\d+\.\s+/, ""), `${i}-${j}`)}
                </li>
              ))}
            </ListTag>
          );
        }
        return <p key={i}>{renderInline(b, i)}</p>;
      })}
    </div>
  );
}

export default PostBody;
